import { AlertIcon, CheckIcon } from './icons'
import {
  fitmentKey,
  fitmentLabel,
  isStaggeredFitment,
  selectionFitmentKey,
  selectionFromFitment,
  type TyreSelection,
} from '../lib/tyres'
import type { TyreFitment } from '../types/PublicBookingChannelTypes'

export const RUN_FLAT_WARNING = "These are run-flat tyres. We'll fit run-flats to match."

export interface TyreFitmentPickerProps {
  /** Already ordered, standard first (see orderFitments). */
  fitments: TyreFitment[]
  selection: TyreSelection | null
  onSelectionChange: (selection: TyreSelection | null) => void
  /** "Not sure / other size": the step switches to typing the size. */
  onOtherSize: () => void
}

/**
 * The car's factory tyre fitments as choices (ACA-98).
 *
 * Every fitment the registration lookup knows, standard first, so a customer
 * with the optional larger wheels finds theirs without reading a sidewall. A
 * staggered car shows both sizes on one choice, because the two axles are one
 * decision. Anyone whose car is not on the list, or who is not sure, can type
 * the size instead.
 */
export function TyreFitmentPicker({
  fitments,
  selection,
  onSelectionChange,
  onOtherSize,
}: TyreFitmentPickerProps) {
  const selectedKey = selectionFitmentKey(selection)

  return (
    <div className="ac-stack ac-stack--tight">
      <ul className="ac-list">
        {fitments.map((fitment) => {
          const key = fitmentKey(fitment)
          const selected = selectedKey === key
          const staggered = isStaggeredFitment(fitment)

          return (
            <li key={key}>
              <button
                type="button"
                className={`ac-option${selected ? ' ac-option--selected' : ''}`}
                aria-pressed={selected}
                onClick={() => onSelectionChange(selectionFromFitment(fitment))}
              >
                <span className="ac-option__check" aria-hidden="true">
                  <CheckIcon size={15} />
                </span>
                <span className="ac-option__main">
                  <span className="ac-option__name">{fitmentLabel(fitment)}</span>
                  <span className="ac-option__desc">
                    {staggered ? 'Different sizes front and rear' : 'All four wheels'}
                  </span>
                  {fitment.runFlat ? (
                    <span className="ac-option__tags">
                      <span className="ac-tag">Run-flat</span>
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      {selection?.runFlat && selectedKey ? (
        <div className="ac-notice ac-notice--info" role="note">
          <AlertIcon size={18} />
          <span>{RUN_FLAT_WARNING}</span>
        </div>
      ) : null}

      <button type="button" className="ac-btn ac-btn--ghost" onClick={onOtherSize}>
        Not sure / other size
      </button>
    </div>
  )
}
